import {
    checkDateInput,
    dateFormatForDB,
    dateFromToday,
    datePlusDays,
    isTodayOrEarlier,
    isValidDate, todayStart
} from '../../../../helpers/date'
import TinyDatePicker from "tiny-date-picker";
import {InquiryMultiField} from "./inquiry_multi_field";

export const DatesFieldConfig = {
    baseHtmlClass: 'inquiry_field--dates',
    selector: '.inquiry_field--dates',

    beginningInputNameAttribute: 'inquiry[booking_checkin_attributes][beginning_at]',
    finishingInputNameAttribute: 'inquiry[booking_checkin_attributes][finishing_at]',
};

export class DatesField extends InquiryMultiField {

    // Creation helpers…

    static new(inquiryForm) {
        if (!inquiryForm.element) { return null };
        const element = inquiryForm.element.querySelector(DatesFieldConfig.selector);
        if (element) { return new DatesField(element, inquiryForm) };
    }

    constructor(id_or_element, inquiryForm) {
        super(id_or_element, inquiryForm);

        // The name attribute on the beginning and finishing inputs may differ (due to AirportRentals form differing from the Inquiry Form).
        // So we set up values for the names now that are later used in `querySelector` calls to find the specific input elements (eg. getBeginningAtInput).
        this.beginningInputNameAttribute = this.inputElements[0].getAttribute('name'); // First input element is the beginning input.
        this.finishingInputNameAttribute = this.inputElements[1].getAttribute('name'); // Second input element is the finishing input.

        this.dateInputSupported = checkDateInput(); // Does the browser support input type='date'.
        if ( !this.dateInputSupported ) { this.setUpDatePickers() }; // If date inputs are not supported we install our polyfill date pickers.

        this.lowAvailabilityDays = 7;
        this.defaultDurationInDays = 7;
        if (!this.hasValue()) { this.setDefaultValue() }

        // Listen for changes to the beginning input and ensure the finishing date is automatically set to an appropriate value.
        const beginningInput = this.getBeginningAtInput();
        if (beginningInput) { beginningInput.addEventListener('change', (event) => { this.adjustFinishingForBeginning(event.target.value) }) };

        this.update();
        this.render();
    }



    // Value handling…

    // Returns the single value as calculated by looking at each of the inputs.
    getInputsValue() {
        const values = this.getInputsValues();
        if (values.length < 1) { return null }
        return (values);
    }

    typecastValue(value) {
        value = new Date(value);
        if (isValidDate(value)) {
            value.setHours(12,0,0,0); // Set to midday
        } else {
            value = null;
        }
        return value;
    }

    // Sets the value for each of the inputs, from the passed in array of values eg. ['2019-08-14', '2019-08-15'].
    setInputsValues(values) {
        // When date inputs are not supported we must update the visible inputs manually.
        if (!this.dateInputSupported) { this.setVisibleInputsValues(values) };
        super.setInputsValues(values);
    }

    humanValue() {
        if (!this.value) { return };
        let humanValues = [];
        this.value.forEach((v)=>{
            if (isValidDate(v)) {
                humanValues.push(v.toLocaleDateString());
            }
        });
        return humanValues.join(' → ');
    }


    // Errors and Warnings…

    appendErrors() {
        const beginningAtValue = this.getBeginningAtInputValue();
        const finishingAtValue = this.getFinishingAtInputValue();
        if (DatesField.isInvalidDates(beginningAtValue, finishingAtValue)) { this.errors.push(`Dates must be valid.`) };
        if (DatesField.beginningAtIsLaterThanFinishingAt(beginningAtValue, finishingAtValue)) { this.errors.push(`Beginning date can not be later than the finishing date.`) };
        if (DatesField.finishingAtIsEarlierThanBeginningAt(beginningAtValue, finishingAtValue)) { this.errors.push(`Finishing date can not be earlier than the beginning date.`) };
        if (DatesField.isTodayOrEarlier(beginningAtValue, finishingAtValue)) { this.errors.push(`Dates can not be today or a date in the past.`) };
    }

    appendWarnings() {
        const beginningAtValue = this.getBeginningAtInputValue();
        if (DatesField.isInNextDays(beginningAtValue, this.lowAvailabilityDays)) { this.warnings.push(`${ this.label } is within the next ${ this.lowAvailabilityDays } days. You may find there is limited availability.`) };
    }



    // Input and value accessors for individual inputs…

    // Beginning at…

    getBeginningInputNameAttribute() {
        return this.beginningInputNameAttribute || DatesFieldConfig.beginningInputNameAttribute;
    }

    getBeginningAtInput() {
        let beginningInput = null;
        this.inputElements.forEach( (input) => {
            if (input.getAttribute('name') === this.getBeginningInputNameAttribute()) { beginningInput = input };
        });
        return beginningInput;
    }

    getBeginningAtInputValue() {
        const input = this.getBeginningAtInput();
        const value = input ? input.value : null;
        return this.typecastValue(value);
    }

    setBeginningAtInputValue(value) {
        const input = this.getBeginningAtInput();
        if (!this.dateInputSupported) { this.setVisibleBeginningInputValue(value) }; // When date inputs are not supported we must update the visible date input too.
        if (input) { input.value = value };
    }

    // Finishing at…

    getFinishingInputNameAttribute() {
        return this.finishingInputNameAttribute || DatesFieldConfig.finishingInputNameAttribute;
    }

    getFinishingAtInput() {
        let finishingInput = null;
        this.inputElements.forEach( (input) => {
            if (input.getAttribute('name') === this.getFinishingInputNameAttribute()) { finishingInput = input };
        });
        return finishingInput;
    }

    getFinishingAtInputValue() {
        const input = this.getFinishingAtInput();
        const value = input ? input.value : null;
        return this.typecastValue(value);
    }

    setFinishingAtInputValue(value) {
        const input = this.getFinishingAtInput();
        if (!this.dateInputSupported) { this.setVisibleFinishingInputValue(value) }; // When date inputs are not supported we must update the visible date input too.
        if (input) { input.value = value };
    }




    // Set up default input values…
    // To set the default field value we set the input values and then set the field value based on those inputs.
    setDefaultValue() {
        const beginningDate = dateFromToday(this.lowAvailabilityDays);
        const finishingDate = datePlusDays(beginningDate, this.defaultDurationInDays);
        const values = [ dateFormatForDB(beginningDate), dateFormatForDB(finishingDate) ];
        this.setInputsValues(values);
        this.setValueFromInputs();
    }




    // Set the finishing at date to a value that is X day/s later than a supplied beginning at value.
    adjustFinishingForBeginning(beginningValue, daysLater=0) {
        const beginningDate = this.typecastValue(beginningValue);
        if (!isValidDate(beginningDate)) { return };
        const finishingDate = this.getFinishingAtInputValue();
        if (!finishingDate || beginningDate.getTime() >= finishingDate.getTime()) {
            const newDate = datePlusDays(beginningDate, daysLater);
            this.setFinishingAtInputValue(dateFormatForDB(newDate));
            this.changeListenerReference();
        }
    }




    // Methods related to polyfilling the date input support…

    // The 'date' input type is not supported by the browser so we use a date picker library as a polyfill.
    // The technique is to create a hidden input for each visible date input.
    // The visible date is presented to the user and the hidden input value is passed to the server.
    // `this.inputElements` is initially the visible date inputs, but we replace this with the hidden inputs.
    // Now this class will `this.getInputsValues` from the hidden inputs and therefore all the values will come from the hidden inputs.
    // When setting the input values we need to also manually update the visible inputs (see `setInputsValues` and `setFinishingAtInputValue`).
    setUpDatePickers() {
        if (this.dateInputSupported) { return };

        const inputs = this.inputElements; // We get the original date input elements (these will be used for displaying to the user).
        let hiddenInputElements = []; // We'll collect some hidden inputs which will be used for values and for passing to the server. This will replace `this.inputElements`.

        inputs.forEach( (input) => {

            // Remove the inherited change event listener - we'll be using the hidden input for listening for changes.
            input.removeEventListener('change', this.changeListenerReference);

            // We change the input type to text (presumably from 'date') so we can show any date string without worrying about formatting for the server.
            input.type = "text";
            input.placeholder = 'Choose Date';

            // We create a hidden input to take the date as a formatted string for the server and also for setting the values.
            const hiddenInput = InquiryMultiField.createHiddenInputElement(input.name);
            hiddenInputElements.push(hiddenInput);
            this.element.appendChild(hiddenInput); // it's important that the hidden inputs are inserted below the initial date inputs.

            // When the hidden input changes then we handle it the same way as we handle changes to the original date input.
            hiddenInput.addEventListener('change', this.changeListenerReference);

            // https://github.com/chrisdavies/tiny-date-picker/blob/master/docs/tiny-date-picker.md
            // Set up our date picker instance and when a date is selected we update the hidden input with the formatted date for the server.
            const datePicker = TinyDatePicker(input, {
                mode: 'dp-below',
                format: (date) => date.toLocaleDateString(),
                parse: (str) => { return this.typecastValue(hiddenInput.value); },
                min: new Date()
            });

            datePicker.on('statechange', (_, dp) => {
                const date = dp.state.selectedDate;
                const visibleDate = (isValidDate(date) ? date.toLocaleDateString() : '');
                input.value = visibleDate; // Visible value is set for effect.
                hiddenInput.value = dateFormatForDB(date);
                hiddenInput.dispatchEvent(new Event('change'));
            });

        });

        // The hidden inputs need to be used for getting the values, so we override the `this.inputElements` value.
        this.inputElements = hiddenInputElements;
    }

    setVisibleInputsValues(values) {
        // We're assuming the first value is `beginning_at` date and second is `finishing_at` date.
        this.setVisibleBeginningInputValue(values[0]);
        this.setVisibleFinishingInputValue(values[1]);
    }

    // Set the visible beginning date input for our user to see.
    // Only used when browser doesn't offer date input support.
    setVisibleBeginningInputValue(beginningValue) {
        const visibleBeginningInput = this.element.querySelector(`input[name="${ this.getBeginningInputNameAttribute() }"]`); // `querySelector` finds the first match - which is our visible input.
        if (visibleBeginningInput) {
            const beginningDate = this.typecastValue(beginningValue);
            const visibleBeginningDate = (isValidDate(beginningDate) ? beginningDate.toLocaleDateString() : '');
            visibleBeginningInput.value = visibleBeginningDate;
        };
    }

    // Set the visible finishing date input for our user to see.
    // Only used when browser doesn't offer date input support.
    setVisibleFinishingInputValue(finishingValue) {
        const visibleFinishingInput = this.element.querySelector(`input[name="${ this.getFinishingInputNameAttribute() }"]`); // `querySelector` finds the first match - which is our visible input.
        if (visibleFinishingInput) {
            const finishingDate = this.typecastValue(finishingValue);
            const visibleFinishingDate = (isValidDate(finishingDate) ? finishingDate.toLocaleDateString() : '');
            visibleFinishingInput.value = visibleFinishingDate;
        };
    }




    // Validation helpers…

    static isInvalidDates(beginningAtValue, finishingAtValue) {
        return ( !isValidDate(finishingAtValue) && !isValidDate(beginningAtValue) );
    }

    static beginningAtIsLaterThanFinishingAt(beginningAtValue, finishingAtValue) {
        if (beginningAtValue && finishingAtValue) {
            beginningAtValue.setHours(0,0,0,0) // get date at start of the day.
            finishingAtValue.setHours(0,0,0,0) // get date at start of the day.
            if (beginningAtValue.getTime() > finishingAtValue.getTime()) { return true };
        }
        return false;
    }

    static finishingAtIsEarlierThanBeginningAt(beginningAtValue, finishingAtValue) {
        if (beginningAtValue && finishingAtValue) {
            beginningAtValue.setHours(23,59,59,999) // get date at end of the day.
            finishingAtValue.setHours(23,59,59,999) // get date at end of the day.
            if (finishingAtValue.getTime() < beginningAtValue.getTime()) { return true }
        }
        return false;
    }

    static finishingAtIsSameOrEarlierThanBeginningAt(beginningAtValue, finishingAtValue) {
        if (beginningAtValue && finishingAtValue) {
            beginningAtValue.setHours(23,59,59,999) // get date at end of the day.
            finishingAtValue.setHours(23,59,59,999) // get date at end of the day.
            if (finishingAtValue.getTime() <= beginningAtValue.getTime()) { return true };
        }
        return false;
    }

    static isTodayOrEarlier(beginningAtValue, finishingAtValue) {
        if (beginningAtValue && finishingAtValue) {
            beginningAtValue.setHours(0, 0, 0, 0) // get date at start of the day.
            finishingAtValue.setHours(0, 0, 0, 0) // get date at start of the day.
            return isTodayOrEarlier(beginningAtValue) || isTodayOrEarlier(finishingAtValue);
        }
        return false;
    }

    static isInNextDays(beginningAtValue, days=1) {
        if (beginningAtValue) {
            beginningAtValue.setHours(0,0,0,0) // get date at start of the day.
            if (beginningAtValue.getTime() < todayStart().getTime()) { return false }; // No warning when beginning at date is in the past.
            if (beginningAtValue.getTime() < dateFromToday(days).getTime()) { return true };
        }
        return false;
    }

}
